import { useApiWithHandling } from "../hooks/useApiWithHandling";
import { useCallback } from "react";

// Type definitions matching the backend CategoryDto
export interface Category {
  code: string;
  name: string;
  description: string;
  requiresSerialNumber: boolean;
  parentCode?: string;
  assemblyType?: string;
  componentId?: string;
  subAssemblyId?: string;
}

export interface CategoryApiResponse {
  data: Category[];
  success: boolean;
  message?: string;
}

/**
 * Custom hook for category-related operations
 * Fetches IATA standard categories from the metadata endpoint
 */
export const useCategoryService = () => {
  const { get } = useApiWithHandling();

  /**
   * Fetch all standard IATA categories for aircraft document organization
   * @returns Promise with array of categories
   */
  const getStandardCategories = useCallback(async (): Promise<Category[] | null> => {
    const endpoint = "metadata/categories";

    const response = await get<CategoryApiResponse>(
      endpoint,
      {
        showLoader: true,
        loadingMessage: "Loading categories...",
        showErrorSnackbar: true,
      },
      true // requireAuth
    );

    if (response?.data?.success && response.data.data) {
      return response.data.data;
    }

    return null;
  }, [get]);

  /**
 * Fetch upload folders for a specific tail number
 */
const getUploadFolders = useCallback(async (tailNumber: string): Promise<Category[] | null> => {
  const endpoint = `metadata/upload-folders/${tailNumber}`;

  const response = await get<CategoryApiResponse>(
    endpoint,
    {
      showLoader: true,
      loadingMessage: "Loading upload folders...",
      showErrorSnackbar: true,
    },
    true // requireAuth
  );

  if (response?.data?.success && response.data.data) {
    return response.data.data;
  }

  return null;
}, [get]);

  /**
   * Get category by code
   * @param code - Category code (A-Z)
   * @returns Category or null
   */
  const getCategoryByCode = async (code: string): Promise<Category | null> => {
    const categories = await getStandardCategories();
    if (!categories) return null;
    
    return categories.find(c => c.code === code) || null;
  };

  /**
   * Get all unique category codes
   * @returns Array of unique codes
   */
  const getUniqueCategoryCodes = async (): Promise<string[]> => {
    const categories = await getStandardCategories();
    if (!categories) return [];
    
    const codes = categories.map(c => c.code);
    const uniqueCodes = codes.filter((code, index) => codes.indexOf(code) === index);
    return uniqueCodes.sort((a, b) => a.localeCompare(b));
  };

  /**
   * Get categories that require serial numbers (for engine, APU, landing gear)
   * @returns Array of categories requiring serial numbers
   */
  const getCategoriesRequiringSerialNumber = async (): Promise<Category[]> => {
    const categories = await getStandardCategories();
    if (!categories) return [];
    
    return categories.filter(c => c.requiresSerialNumber);
  };

  /**
   * Format category for dropdown display
   * @param category - Category object
   * @returns Formatted label
   */
  const formatCategoryLabel = (category: Category): string => {
    return `${category.code} - ${category.name}`;
  };

  /**
   * One call: serial folders (top-level) + WarehouseCategory children — mirrors getUploadFolders shape.
   * e.g. componentType = "Engine" → [{code:"SN9021832", parentCode:null}, {code:"F001", parentCode:"SN9021832"}, ...]
   */
  const getWarehouseUploadFolders = useCallback(async (componentType: string): Promise<Category[] | null> => {
    const response = await get<CategoryApiResponse>(
      `metadata/warehouse-upload-folders/${encodeURIComponent(componentType)}`,
      { showLoader: true, loadingMessage: "Loading assembly folders...", showErrorSnackbar: true },
      true
    );
    return response?.data?.success ? response.data.data : null;
  }, [get]);

  const getSubAssemblyPoolFolders = useCallback(async (): Promise<Category[] | null> => {
    const response = await get<CategoryApiResponse>(
      `metadata/sub-assembly-pool-folders`,
      { showLoader: true, loadingMessage: "Loading sub-assembly folders...", showErrorSnackbar: true },
      true
    );
    return response?.data?.success ? response.data.data : null;
  }, [get]);

  return {
    getStandardCategories,
    getCategoryByCode,
    getUniqueCategoryCodes,
    getCategoriesRequiringSerialNumber,
    formatCategoryLabel,
    getUploadFolders,
    getWarehouseUploadFolders,
    getSubAssemblyPoolFolders,
  };
};
